import { Box, Button, CircularProgress, Typography } from '@mui/material';
import {
  ExternalResourceType,
  FileEnum,
  ImplementedContentModel,
  PageContentInfo,
} from '@protos/content_management/content.db_pb';
import { lazy, Suspense } from 'react';
import { shouldIncludeCredentialsInRequest } from '../../../commonUtils/images';
import {
  blobToDataUri,
  downloadPdfToAndroid,
  isAndroidDownloadAvailable,
} from '../../../commonUtils/utilFunctions';
import CustomPDFRenderer from './CustomPdfRenderer';
import { ScormPlayer } from '../FilePreviewCard/ScormPlayer';
import { H5pPlayer } from '../H5pPlayer';

const EpubViewer = lazy(() =>
  import('./EpubViewer').then((m) => ({ default: m.EpubViewer }))
);

// File types that can be shown on the page instead of in DocumentViewer's popup.
export const INLINE_VIEWABLE_FILE_TYPES = [
  FileEnum.FILE_TYPE_PDF,
  FileEnum.FILE_TYPE_DOCUMENT,
  FileEnum.FILE_TYPE_PRESENTATION,
  FileEnum.FILE_TYPE_EPUB,
  FileEnum.FILE_TYPE_SCORM,
  FileEnum.FILE_TYPE_H5P,
  FileEnum.FILE_TYPE_HTML,
  FileEnum.FILE_TYPE_IMAGE,
];

// The file to show on the page when a resource is just one inline-viewable file
// (one page, one content, one passage element). Otherwise undefined.
export const getSingleInlineFile = (
  pageContents: PageContentInfo[]
): ExternalResourceType | undefined => {
  if (pageContents.length !== 1) return undefined;
  const contents = pageContents[0].contents ?? [];
  if (contents.length !== 1) return undefined;
  const model = new ImplementedContentModel(contents[0]).model;
  if (model.case !== 'content') return undefined;
  const contentModel = model.value.content?.model;
  if (contentModel?.case !== 'passage') return undefined;
  const elements = contentModel.value.elements;
  if (elements.length !== 1) return undefined;
  const element = elements[0].model;
  if (element.case !== 'externalResource') return undefined;
  if (!INLINE_VIEWABLE_FILE_TYPES.includes(element.value.fileType))
    return undefined;
  return element.value;
};

// Extracted packages are folders, so there is no single file to download.
const PACKAGE_FILE_TYPES = [
  FileEnum.FILE_TYPE_SCORM,
  FileEnum.FILE_TYPE_H5P,
  FileEnum.FILE_TYPE_HTML,
];

interface IProps {
  type: FileEnum;
  gcpUrl: string;
  previewUrl?: string;
  fileName?: string;
  disableDownload?: boolean;
  // EPUB only: run the book's scripts. Trusted content only, see EpubViewer.
  allowScripts?: boolean;
}

// Same renderers as DocumentViewer, without the popup. Fills a fixed-height area.
export const InlineFileViewer = (props: IProps) => {
  const { type, gcpUrl, previewUrl, fileName, disableDownload, allowScripts } =
    props;
  const canDownload = !disableDownload && !PACKAGE_FILE_TYPES.includes(type);

  const handleDownload = async () => {
    const name = fileName || gcpUrl.split('/').pop() || 'downloaded-file';
    try {
      const options: RequestInit = {};
      if (shouldIncludeCredentialsInRequest(gcpUrl)) {
        options.credentials = 'include';
      }
      const response = await fetch(gcpUrl, options);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      if (isAndroidDownloadAvailable()) {
        downloadPdfToAndroid(await blobToDataUri(blob), name);
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('There was a problem downloading the file:', error);
    }
  };

  const renderViewer = () => {
    switch (type) {
      case FileEnum.FILE_TYPE_PDF:
        return <CustomPDFRenderer file={gcpUrl} layoutType="multiple" />;
      case FileEnum.FILE_TYPE_DOCUMENT:
      case FileEnum.FILE_TYPE_PRESENTATION:
        return previewUrl ? (
          <CustomPDFRenderer file={previewUrl} layoutType="multiple" />
        ) : (
          <Typography variant="elementBodyText" textAlign="center" mt={4}>
            Preview not available please download the file to view
          </Typography>
        );
      case FileEnum.FILE_TYPE_EPUB:
        return (
          <Suspense fallback={<CircularProgress />}>
            <EpubViewer url={gcpUrl} allowScripts={allowScripts} />
          </Suspense>
        );
      case FileEnum.FILE_TYPE_SCORM:
        return <ScormPlayer folderUrl={gcpUrl} />;
      case FileEnum.FILE_TYPE_H5P:
        return <H5pPlayer contentBaseUrl={gcpUrl} height={'100%'} />;
      case FileEnum.FILE_TYPE_HTML:
        return (
          <iframe
            title={fileName || 'HTML Viewer'}
            src={`${gcpUrl}/${fileName || 'index.html'}`}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          />
        );
      case FileEnum.FILE_TYPE_IMAGE:
        return (
          <img
            src={gcpUrl}
            alt={fileName || 'Image'}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              display: 'block',
              margin: '0 auto',
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}
    >
      {canDownload && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" size="small" onClick={handleDownload}>
            Download
          </Button>
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          height: '80vh',
          overflow: 'auto',
          border: '1px solid #CCE6FE',
          borderRadius: '10px',
          boxSizing: 'border-box',
        }}
      >
        {renderViewer()}
      </Box>
    </Box>
  );
};
